import { useEffect, useRef, useState, type ClipboardEvent } from 'react';
import { api, apiUrl, ApiClientError } from '../lib/api';

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

function imageType(file: File): string {
  const ext = file.name.split('.').at(-1)?.toLowerCase();
  if (file.type === 'image/png' || ext === 'png') return 'image/png';
  if (file.type === 'image/webp' || ext === 'webp') return 'image/webp';
  return 'image/jpeg';
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character] ?? character));
}

/** Keep the summary document deliberately small and predictable before it reaches the API. */
export function sanitizeSummaryHtml(value: string): string {
  if (typeof DOMParser === 'undefined') return value.replace(/<[^>]*>/g, '');
  const doc = new DOMParser().parseFromString(value, 'text/html');
  const allowed = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'H2', 'H3', 'UL', 'OL', 'LI', 'A', 'IMG']);
  for (const element of Array.from(doc.body.querySelectorAll('*'))) {
    if (!allowed.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      continue;
    }
    const originalHref = element.tagName === 'A' ? element.getAttribute('href') : null;
    const originalSrc = element.tagName === 'IMG' ? element.getAttribute('src') : null;
    for (const attribute of Array.from(element.attributes)) element.removeAttribute(attribute.name);
    if (element.tagName === 'A') {
      const href = originalHref;
      if (href && /^https:\/\//i.test(href)) {
        element.setAttribute('href', href);
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noreferrer noopener');
      }
    }
    if (element.tagName === 'IMG') {
      const src = originalSrc;
      if (!src || !/^\/api\/files\/[0-9a-f-]+\/content\?inline=true$/i.test(src)) element.remove();
      else {
        element.setAttribute('src', src);
        element.setAttribute('alt', 'รูปประกอบสรุป');
        element.setAttribute('loading', 'lazy');
      }
    }
  }
  return doc.body.innerHTML;
}

async function sha256(file: File): Promise<string> {
  const { createSHA256 } = await import('hash-wasm');
  const hash = await createSHA256();
  hash.init();
  hash.update(new Uint8Array(await file.arrayBuffer()));
  return hash.digest();
}

async function uploadImage(meetingId: string, file: File): Promise<string> {
  if (file.size > MAX_IMAGE_BYTES) throw new Error('รูปมีขนาดเกิน 10 MB');
  const contentType = imageType(file);
  const extension = contentType === 'image/png' ? 'png' : contentType === 'image/webp' ? 'webp' : 'jpg';
  const normalized = new File([file], `summary-${crypto.randomUUID()}.${extension}`, { type: contentType });
  const started = await api<{ data: { id: string; provider: 'r2' | 'nas'; uploadUrl?: string; partBytes?: number; totalParts?: number; authorizePartUrl?: string; completeUrl?: string } }>('/api/files/uploads', {
    method: 'POST',
    body: JSON.stringify({ fileName: normalized.name, contentType, sizeBytes: normalized.size, sha256: await sha256(normalized), purpose: 'meeting_attachment', category: 'attachment', linkedEntityType: 'meeting', linkedEntityId: meetingId }),
  });
  const upload = started.data;
  if (upload.provider === 'r2' && upload.uploadUrl) {
    const response = await fetch(apiUrl(upload.uploadUrl), { method: 'PUT', credentials: 'include', headers: { 'Content-Type': contentType }, body: normalized });
    if (!response.ok) throw new Error('อัปโหลดรูปไม่สำเร็จ');
  } else {
    if (!upload.partBytes || !upload.totalParts || !upload.authorizePartUrl || !upload.completeUrl) throw new Error('ข้อมูลอัปโหลดรูปไม่ครบถ้วน');
    for (let part = 1; part <= upload.totalParts; part += 1) {
      const authorized = await api<{ data: { url: string; token: string } }>(upload.authorizePartUrl.replace('{part}', String(part)), { method: 'POST' });
      const response = await fetch(authorized.data.url, { method: 'PUT', headers: { Authorization: `Bearer ${authorized.data.token}`, 'Content-Type': 'application/octet-stream' }, body: normalized.slice((part - 1) * upload.partBytes, Math.min(part * upload.partBytes, normalized.size)) });
      if (!response.ok) throw new Error('อัปโหลดรูปไป NAS ไม่สำเร็จ');
    }
    await api(upload.completeUrl, { method: 'POST' });
  }
  return apiUrl(`/api/files/${upload.id}/content?inline=true`);
}

interface SummaryEditorModalProps {
  meetingId: string;
  initialValue: string;
  title?: string;
  saving?: boolean;
  onCancel: () => void;
  onSave: (value: string) => void;
}

export function SummaryEditorModal({ meetingId, initialValue, title = 'แก้ไขสรุปการประชุม', saving = false, onCancel, onSave }: SummaryEditorModalProps) {
  const editor = useRef<HTMLDivElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (editor.current) editor.current.innerHTML = initialValue.trim().startsWith('<') ? sanitizeSummaryHtml(initialValue) : `<p>${escapeHtml(initialValue).replace(/\n/g, '<br>')}</p>`;
  }, [initialValue]);
  const command = (name: string, value?: string) => { editor.current?.focus(); document.execCommand(name, false, value); };
  const onPaste = async (event: ClipboardEvent<HTMLDivElement>) => {
    const image = Array.from(event.clipboardData.items).find((item) => item.type.startsWith('image/'));
    if (!image) return;
    event.preventDefault();
    const file = image.getAsFile();
    if (!file || !editor.current) return;
    setUploading(true); setError(null);
    try {
      const src = await uploadImage(meetingId, file);
      editor.current.focus();
      document.execCommand('insertHTML', false, `<img src="${src.replace(apiUrl(''), '')}" alt="รูปประกอบสรุป" loading="lazy">`);
    } catch (uploadError) {
      setError(uploadError instanceof ApiClientError ? uploadError.message : uploadError instanceof Error ? uploadError.message : 'วางรูปไม่สำเร็จ');
    } finally { setUploading(false); }
  };
  const save = () => onSave(sanitizeSummaryHtml(editor.current?.innerHTML ?? ''));
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/60 p-3 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="summary-editor-title">
      <div className="mx-auto flex h-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 sm:px-6">
          <div><h2 id="summary-editor-title" className="text-lg font-bold">{title}</h2><p className="text-xs text-slate-500">วางรูปจาก clipboard ได้โดยตรง · หน้าต่างนี้ปิดด้วยปุ่มด้านล่างเท่านั้น</p></div>
        </div>
        <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 px-4 py-2 sm:px-6">
          {[['bold', 'ตัวหนา'], ['italic', 'ตัวเอียง'], ['underline', 'ขีดเส้นใต้'], ['insertUnorderedList', '• รายการ'], ['insertOrderedList', '1. รายการ']].map(([name, label]) => <button key={name} type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:border-teal-400" onMouseDown={(event) => { event.preventDefault(); command(name); }}>{label}</button>)}
          <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:border-teal-400" onMouseDown={(event) => { event.preventDefault(); command('formatBlock', 'H2'); }}>หัวข้อ</button>
          <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:border-teal-400" onMouseDown={(event) => { event.preventDefault(); const url = window.prompt('วางลิงก์ https://'); if (url && /^https:\/\//i.test(url)) command('createLink', url); }}>ลิงก์</button>
        </div>
        <div ref={editor} contentEditable suppressContentEditableWarning onPaste={onPaste} className="prose max-w-none flex-1 overflow-y-auto p-5 outline-none sm:p-8" data-placeholder="พิมพ์สรุปการประชุมที่นี่…" />
        {uploading && <p className="border-t border-slate-200 bg-teal-50 px-4 py-2 text-sm text-teal-800">กำลังอัปโหลดรูปที่วาง…</p>}
        {error && <p className="border-t border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-6"><button type="button" className="btn-secondary" disabled={saving || uploading} onClick={onCancel}>ยกเลิก</button><button type="button" className="btn-primary" disabled={saving || uploading} onClick={save}>{saving ? 'กำลังบันทึก…' : 'ปิดและบันทึก'}</button></div>
      </div>
    </div>
  );
}
